import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, ChevronRight, LayoutGrid } from "lucide-react";
import { api } from "@/lib/api";
import { imgUrl } from "@/lib/format";
import ProductCard from "@/components/ProductCard";

const ALL = "__all__";
const psubs = (p) => (p.subcategories?.length ? p.subcategories : (p.subcategory ? [p.subcategory] : [])).map((s) => (s || "").trim()).filter(Boolean);

export default function CategoryProducts() {
  const { name } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState(null);
  const cat = decodeURIComponent(name);
  const subParam = searchParams.get("sub") || "";
  const [activeSub, setActiveSub] = useState(subParam);
  const [activeSubsub, setActiveSubsub] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get("/products", { params: { category: cat } })
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
    api.get("/categories").then((r) => setCategory((r.data || []).find((c) => c.name === cat) || null));
  }, [cat]);

  useEffect(() => { setActiveSub(subParam); }, [subParam, cat]);
  useEffect(() => { setActiveSubsub(""); }, [activeSub, cat]);

  // subcategories present in this category's products, ordered by the managed list when possible
  const subs = useMemo(() => {
    const present = new Set(products.flatMap(psubs));
    const managed = (category?.subcategories || []).filter((s) => present.has(s));
    const extra = [...present].filter((s) => !managed.includes(s)).sort();
    return [...managed, ...extra];
  }, [products, category]);

  const countFor = (s) => products.filter((p) => psubs(p).includes(s)).length;

  const setSub = (s) => {
    setActiveSub(s);
    const p = new URLSearchParams(searchParams);
    if (s && s !== ALL) p.set("sub", s); else if (s === ALL) p.set("sub", ALL); else p.delete("sub");
    setSearchParams(p, { replace: true });
  };

  const showChooser = subs.length > 0 && !activeSub;
  const shownBySub = activeSub && activeSub !== ALL
    ? products.filter((p) => psubs(p).includes(activeSub))
    : products;
  const childList = (activeSub && activeSub !== ALL && category?.sub_children) ? (category.sub_children[activeSub] || []) : [];
  const presentChildren = childList.filter((ch) => shownBySub.some((p) => (p.subsub || "").trim() === ch));
  const shown = activeSubsub ? shownBySub.filter((p) => (p.subsub || "").trim() === activeSubsub) : shownBySub;

  return (
    <div>
      <button
        onClick={() => navigate(-1)}
        data-testid="category-back"
        className="flex items-center gap-1 text-sm text-muted-foreground hover:text-gold mb-4 transition-colors"
      >
        <ArrowLeft size={16} /> Geri
      </button>

      <h1 className="font-display text-3xl text-foreground mb-1">{cat}</h1>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="animate-spin text-gold" size={28} /></div>
      ) : showChooser ? (
        <>
          <p className="text-sm text-muted-foreground mb-6">Bir alt kategori seçin.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="subcategory-chooser">
            {subs.map((s, i) => {
              const sample = products.find((p) => psubs(p).includes(s) && p.image_url);
              return (
                <button
                  key={s}
                  data-testid={`subchoose-${s}`}
                  onClick={() => setSub(s)}
                  className="group relative rounded-md overflow-hidden border border-border aspect-[3/2] animate-fade-up"
                  style={{ animationDelay: `${Math.min(i, 12) * 40}ms` }}
                >
                  {sample?.image_url
                    ? <img src={imgUrl(sample.image_url)} alt={s} className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                    : <div className="absolute inset-0 bg-elevated" />}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-3 flex items-center justify-between">
                    <span className="text-white font-medium text-sm">{s} <span className="text-white/60">({countFor(s)})</span></span>
                    <ChevronRight size={16} className="text-gold" />
                  </div>
                </button>
              );
            })}
            <button
              data-testid="subchoose-all"
              onClick={() => setSub(ALL)}
              className="rounded-md border border-dashed border-gold/40 aspect-[3/2] flex flex-col items-center justify-center gap-2 text-gold hover:bg-gold/5 transition-colors animate-fade-up"
              style={{ animationDelay: `${Math.min(subs.length, 12) * 40}ms` }}
            >
              <LayoutGrid size={22} />
              <span className="text-sm font-medium">Tüm Ürünler ({products.length})</span>
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-muted-foreground mb-4">{shown.length} ürün</p>

          {subs.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6" data-testid="subcategory-filters">
              <button
                data-testid="subfilter-all"
                onClick={() => setSub(ALL)}
                className={`px-3.5 h-8 rounded-full text-xs border transition-colors ${(activeSub === ALL || activeSub === "") ? "bg-gold text-obsidian border-gold" : "border-border text-muted-foreground hover:border-gold hover:text-gold"}`}
              >
                Tümü
              </button>
              {subs.map((s) => (
                <button
                  key={s}
                  data-testid={`subfilter-${s}`}
                  onClick={() => setSub(s)}
                  className={`px-3.5 h-8 rounded-full text-xs border transition-colors ${activeSub === s ? "bg-gold text-obsidian border-gold" : "border-border text-muted-foreground hover:border-gold hover:text-gold"}`}
                >
                  {s} <span className="opacity-60">({countFor(s)})</span>
                </button>
              ))}
            </div>
          )}

          {presentChildren.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6 -mt-2" data-testid="subsub-filters">
              <button
                data-testid="subsubfilter-all"
                onClick={() => setActiveSubsub("")}
                className={`px-3.5 h-8 rounded-full text-xs border transition-colors ${!activeSubsub ? "bg-gold text-obsidian border-gold" : "border-border text-muted-foreground hover:border-gold hover:text-gold"}`}
              >
                Tümü
              </button>
              {presentChildren.map((ch) => (
                <button
                  key={ch}
                  data-testid={`subsubfilter-${ch}`}
                  onClick={() => setActiveSubsub(activeSubsub === ch ? "" : ch)}
                  className={`px-3.5 h-8 rounded-full text-xs border transition-colors ${activeSubsub === ch ? "bg-gold text-obsidian border-gold" : "border-border text-muted-foreground hover:border-gold hover:text-gold"}`}
                >
                  {ch} <span className="opacity-60">({shownBySub.filter((p) => (p.subsub || "").trim() === ch).length})</span>
                </button>
              ))}
            </div>
          )}

          {shown.length === 0 ? (
            <p className="text-muted-foreground text-sm py-12 text-center">Bu kategoride ürün bulunmuyor.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="product-grid">
              {shown.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
